import { InlineLoading } from "@carbon/react";
import { TextError } from "@flaminem/ui-components";
import React from "react";

import { InlineNotification } from "src/components/Notifications/InlineNotification";
import { PropertyValueInput } from "src/graphql/anti-fraud/__generated__/graphql.ts";
import { useGetBlacklistedIdentifiers } from "src/graphql/anti-fraud/queries/cases/GetCaseBlacklistedIdentifiersQuery.ts";
import { useT } from "src/internationalization";
import { BlacklistableCaseIdentifier } from "src/types/case";

import { BlacklistableIdentifiersList } from "./BlacklistableIdentifiersList.tsx";
import { BlacklistIdentifiersFormControl, SelectedIdentifier } from "./useBlacklistIdentifiersForm";

export type BlacklistIdentifiersFormProps = {
  caseId: string;
  blacklistIdentifiersFormControl: BlacklistIdentifiersFormControl;
};

export const BlacklistIdentifiersForm: React.FC<BlacklistIdentifiersFormProps> = ({
  caseId,
  blacklistIdentifiersFormControl,
}) => {
  const t = useT();

  const {
    blacklistableIdentifiers: {
      data: blacklistableIdentifiers,
      loading: loadingBlacklistableIdentifiers,
      error: errorBlacklistableIdentifiers,
    },
    blacklistSchemas,
    methods,
    reset,
  } = blacklistIdentifiersFormControl;

  const {
    data: blacklistedIdentifiers,
    loading: loadingBlacklistedIdentifiers,
    error: errorBlacklistedIdentifiers,
  } = useGetBlacklistedIdentifiers({
    variables: { id: caseId },
  });

  const loading = loadingBlacklistableIdentifiers || loadingBlacklistedIdentifiers;
  const error = errorBlacklistableIdentifiers || errorBlacklistedIdentifiers;

  const selectedIdentifiers = methods.watch("selectedIdentifiers");
  const showValidationErrors = methods.formState.isSubmitted;

  const handleToggle = (identifier: BlacklistableCaseIdentifier) => {
    const current: SelectedIdentifier[] = methods.getValues("selectedIdentifiers");
    const isSelected = current.some((s) => s.id === identifier.id);

    const next: SelectedIdentifier[] = isSelected
      ? current.filter((s) => s.id !== identifier.id)
      : [
          ...current,
          {
            id: identifier.id,
            identifierCode: identifier.identifier.schemaIdentifier.code,
            elementCode: identifier.element.typeCode,
            blacklistCode: identifier.blacklist.code,
            additionalProperties: [],
          },
        ];

    methods.setValue("selectedIdentifiers", next);

    if (methods.formState.isSubmitted) {
      methods.trigger();
    }
  };

  const handleSelectAll = () => {
    const current: SelectedIdentifier[] = methods.getValues("selectedIdentifiers");
    const isNoneSelected = current.length == 0;

    if (isNoneSelected) reset();
    else methods.setValue("selectedIdentifiers", []);

    if (methods.formState.isSubmitted) {
      methods.trigger();
    }
  };

  const handleSaveAdditionalProperties = (identifierId: string, values: PropertyValueInput[]) => {
    const current: SelectedIdentifier[] = methods.getValues("selectedIdentifiers");
    const next = current.map((s) =>
      s.id === identifierId ? { ...s, additionalProperties: values } : s
    );
    methods.setValue("selectedIdentifiers", next);

    if (methods.formState.isSubmitted) {
      methods.trigger();
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full justify-center items-center">
        <div>
          <InlineLoading />
        </div>
      </div>
    );
  }

  if (error || !blacklistableIdentifiers || !blacklistedIdentifiers) {
    return (
      <div className="p-5">
        <TextError message={error?.message ?? ""} />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {methods.formState.errors.selectedIdentifiers && (
        <div className="flex flex-col px-5 pt-5">
          <InlineNotification
            kind="error"
            title={t("general.error.validation.title")}
            subtitle={methods.formState.errors.selectedIdentifiers.message}
            fullWidth
          />
        </div>
      )}
      <BlacklistableIdentifiersList
        caseId={caseId}
        blacklistableIdentifiers={blacklistableIdentifiers}
        alreadyListedIdentifiers={blacklistedIdentifiers}
        blacklistSchemas={blacklistSchemas}
        selectedIdentifiers={selectedIdentifiers}
        showValidationErrors={showValidationErrors}
        onToggle={handleToggle}
        onSaveAdditionalProperties={handleSaveAdditionalProperties}
        handleSelectAll={handleSelectAll}
      />
    </div>
  );
};
